import { Divider } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import ButtonUI from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconCancel from '@material-ui/icons/Cancel';
import CloseIcon from '@material-ui/icons/Close';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import EditIcon from '@material-ui/icons/Edit';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import DollarSignIcon from '@material-ui/icons/AttachMoney';
import CheckIcon from '@material-ui/icons/Check';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import React, { useEffect, useState } from 'react';
import {
  Button,
  FormWithRedirect,
  SaveButton,
  SelectInput,
  useNotify,
  useRefresh,
  useUpdate,
  useRedirect,
} from 'react-admin';
import { Client } from '@twilio/conversations';
import { apiGet, apiPost, apiPut } from '../../brainApi';
import DialogTitle from '../../components/DialogTitle';
import dataProvider from '../../dataProvider';
import {
  getZ3PConfigValue,
  tokenize,
  formatPrice,
  tracRudderStackEvent,
  currentPageInfo,
} from '../../helper';
import SquareCCInput from '../../QuickActions/SquareCCInput';
import PaidOnlineCard from './PaidOnlineCard';

const useStyles = makeStyles((theme) => ({
  textRight: {
    textAlign: 'right',
  },
  serviceList: {
    color: '#888',
    marginBottom: 3,
  },
  listLabel: {
    color: '#637381',
  },
  pointIcon: {
    cursor: 'pointer',
    color: '#637381',
    width: 16.67,
    height: 16.67,
  },
  titleMargin: {
    marginBottom: 20,
    marginTop: 9,
    '& h5': {
      fontWeight: 700,
    },
  },
  actionButtons: {
    justifyContent: 'center',
    padding: '0 24px',
    marginBottom: '20px',
  },
  btnCancel: {
    backgroundColor: '#fdfdfd',
    height: 37,
    marginRight: 10,
    padding: 10,
    color: '#212B36',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    borderRadius: 8,
  },
  boxMargin: {
    marginBottom: 20,
    marginTop: 20,
  },
  iconMargin: {
    marginRight: 16,
  },
  listColor: {
    color: '#212B36',
  },
  payoutInfo: {
    margin: '40px auto',
    '& h5': {
      fontWeight: 700,
    },
  },
  payoutPrice: {
    fontSize: 48,
    color: '#212B36',
    fontWeight: 900,
  },
  saveBtn: {
    backgroundColor: '#FE5F10',
    color: '#ffffff',
    '& .MuiButton-contained.Mui-disabled': {
      color: '#fff !important',
      backgroundColor: '#ECAA89',
    },
  },
  actionBox: {
    position: 'absolute',
    top: 18,
    right: 12,
  },
  dollarIconStyle: {
    color: '#212B36',
    position: 'relative',
    top: '-10px',
  },
  cardDetailBtn: {
    borderRadius: 8,
    border: '1px solid rgba(145, 158, 171, 0.24)',
    padding: '11px 16px',
    fontSize: 15,
  },
  cardDetailBtnIcon: {
    marginLeft: 8,
  },
  cardDetailBox: {
    border: '1px solid rgba(145, 158, 171, 0.16)',
    borderRadius: 8,
    marginBottom: 20,
    padding: 20,
    '& h5': {
      fontSize: 18,
      fontWeight: 700,
      color: 'rgba(33, 43, 54, 1)',
    },
    '& p': {
      fontSize: 16,
      color: 'rgba(99, 115, 129, 1)',
    },
  },
  cardDetailTitle: {
    padding: 24,
    '& button': {
      top: 20,
      right: 16,
    },
  },
  cardDetailDialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
    },
  },
  creditCardButton: {
    marginTop: 5,
    borderRadius: 8,
    padding: '11px 15px',
    background: 'rgb(17, 141, 87)',
    fontSize: 15,
    fontWeight: 700,
    '& svg': {
      marginLeft: 8,
    },
  },
  markPaidButton: {
    borderRadius: 8,
    color: theme.palette.background.default,
    backgroundColor: '#FE5F10',
    border: `1px solid #FE5F10`,
    padding: '11px 15px',
    fontWeight: 600,
    fontSize: 15,
    width: '90%',
    '&:hover': {
      color: theme.palette.background.default,
      border: `1px solid #FE5F10`,
      backgroundColor: '#FE5F10',
    },
  },
  markBtnIconStyle: {
    marginLeft: 8,
  },
  amountPaidDialog: {
    width: '20em',
    height: '7.5em',
  },
}));

const UnifiedPayment = ({ record }) => {
  const {
    customer,
    payment_method,
    square_card_id,
    total_taxed_price_dollars,
    amount_paid_dollars,
    amount_due,
    st_order_id,
    id,
    status,
    services,
    st_to_installer_amount,
    service_order_id,
    source,
  } = record;
  const isFromS360 =
    window?.location.ancestorOrigins?.[0]?.includes('simple360') ||
    window?.location.href.includes('fromS360');
  const hasPaidInstall = services.some(
    (service) => service.service_payment_status === 'PaidInstall',
  );
  const hasNewTireInstallation = services.some(
    (service) =>
      service.display_name?.replace(/\s+/g, ' ').trim() ===
      'New Tire Installation',
  );
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate('scheduled-work');
  const redirect = useRedirect();
  const [isEditing, setIsEditing] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [card, setCard] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [activePaymentMethods, setActivePaymentMethods] = useState([]);

  const isUsingSquare = getZ3PConfigValue('is_using_square');
  const googleReviewUrl = getZ3PConfigValue('google_review_url');
  const bookingAppBaseUrl = getZ3PConfigValue('booking_app_base_url');
  const { square_brand, square_last4 } = customer;
  const { is_square, short_description = 'None selected' } =
    payment_method || {};
  const hasSquareCard = square_brand && square_last4;
  const [isCardModalDetailOpen, setIsCardModalDetailOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [sendGoogleReview, setSendGoogleReview] = useState(true);
  const [openMarkAsPaidModal, setOpenMarkAsPaidModal] = useState(false);
  const [isToken, setIsToken] = useState('null');
  const [conversationComponents, setConversationComponents] = useState([]);
  const [isViewPaymentLoading, setIsViewPaymentLoading] = useState(false);
  const [viewPaymentError, setViewPaymentError] = useState('');
  const [markCompleteError, setMarkCompleteError] = useState('');
  useEffect(() => {
    dataProvider
      .getList('payment-method', {
        filter: { is_active: true },
        sort: { field: 'payment_method_id', order: 'ASC' },
        pagination: { page: 1, perPage: 500 },
      })
      .then(({ data: paymentMethods }) => {
        setActivePaymentMethods(paymentMethods);
      });
    getToken();
  }, [st_order_id, service_order_id, status]);

  useEffect(() => {
    initChat(isToken);
  }, [isToken]);

  const getToken = () => {
    apiGet('/twilio/chat-token').then((res) => {
      if (res.Status === 'Success') {
        setIsToken(res.Token);
      }
    });
  };

  const initChat = async (isToken) => {
    if (isToken) {
      const chatClient = new Client(isToken);
      chatClient.on('conversationJoined', async (joinedConversation) => {
        joinedConversation
          .getMessages()
          .then(async (messagePaginator) => {
            const lastMessage =
              messagePaginator.items.length > 0
                ? messagePaginator.items[
                    messagePaginator?.items?.length - 1
                      ? messagePaginator?.items?.length - 1
                      : 0
                  ]
                : {};

            // Add joined conversation to state

            setConversationComponents((conversationComponents) => [
              ...conversationComponents,
              { baseConversation: joinedConversation, lastMessage },
            ]);
          })
          .catch((err) => {
            console.error("Couldn't fetch messages IMPLEMENT RETRY", err);
            // eslint-disable-next-line react/no-this-in-sfc
            this.setState({ isListLoading: false });
          });
      });
    }
  };

  const addCreditCardToCustomer = (token) => {
    const payload = {
      square_token: token,
      sw_id: record.id,
    };
    return apiPost(`/add-square-card/${customer.id}`, payload).then((json) => {
      if (json.success) {
        notify('Card added');
      } else {
        throw new Error(json.message);
      }
    });
  };

  const handleSubmit = async (values) => {
    update(
      {
        payload: {
          id: record.id,
          data: values,
          previousData: record,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          notify('Payment method updated successfully');
          refresh();
        },
        onFailure: ({ error }) => {
          notify(error.message, 'error');
        },
      },
    );
  };

  const submitCard = async (card_) => {
    setIsSaving(true);
    setErrorMessage('');
    try {
      const token = await tokenize(card_);
      await addCreditCardToCustomer(token);
      if (!is_square) {
        const squarePaymentMethodId = activePaymentMethods.find(
          (pm) => pm.is_square,
        )?.id;
        handleSubmit({ payment_method_id: squarePaymentMethodId });
      }
      refresh();
      setIsCardModalOpen(false);
    } catch {
      setErrorMessage('Failed to add card');
    } finally {
      setIsSaving(false);
    }
  };

  const closeModal = () => {
    setErrorMessage('');
    setIsCardModalOpen(false);
  };
  const closeDetailModal = () => {
    setIsCardModalDetailOpen(false);
  };

  const completeRepairOrder = (st_order_id = null) => {
    if (st_order_id) {
      // handlePaidAmount(st_order_id);
      setOpenMarkAsPaidModal(true);
    } else {
      setOpenMarkAsPaidModal(true);
    }
  };

  const chatFirstServiceName = services[0].display_name;
  const reviewUrl = `${bookingAppBaseUrl}/google-review?utm_source=sms&utm_campaign=gmb_reviews`;
  const googleReviewMessageBody =
    `Thank you for choosing us for your ${chatFirstServiceName}. We hope you're satisfied with the results.` +
    `\nPlease let us know your thoughts here ${reviewUrl ?? ''}`;

  const payload = {
    amount_paid_dollars: `${total_taxed_price_dollars}`,
    google_review_message_body: googleReviewMessageBody,
    send_google_review_url: sendGoogleReview,
    send_receipt: sendReceipt,
    status: 'finished',
  };
  const rudderStackEvent = (action = '', appointmentId = 0) => {
    tracRudderStackEvent('reciept_modal_viewed', {
      proname: getZ3PConfigValue('z3p_client_name'),
      installer_id: getZ3PConfigValue('st_installer_id'),
      Click_selection: action,
      Customer_receipt: sendReceipt,
      Review_request: sendGoogleReview,
      page_information: {
        path: currentPageInfo.PATH,
        url: currentPageInfo.URL,
        // eslint-disable-next-line no-undef
        title: document.title,
      },
    });
    if (sendGoogleReview && action == 'Confirm') {
      apiGet(`/scheduled-work/${appointmentId}`)
        .then((scheduledWorkRes) => {
          const { customer } = scheduledWorkRes;
          tracRudderStackEvent('Review_SMS_requested', {
            proname: getZ3PConfigValue('z3p_client_name'),
            installer_id: getZ3PConfigValue('st_installer_id'),
            customer_email: customer?.email,
            phone_number: customer?.phone,
            First_name: customer?.first_name,
            Last_name: customer?.last_name,
            withAppointment: true,
            page_information: {
              path: currentPageInfo.PATH,
              url: currentPageInfo.URL,
              title: document.title,
            },
          });
        })
        .catch((error) => {
          console.warn(
            'Error while rudderstack (Review_SMS_requested) event call.',
          );
        });
    }
  };

  const handleCancelReceiptModel = () => {
    rudderStackEvent('Cancel');
    setOpenMarkAsPaidModal(false);
  };

  // eslint-disable-next-line no-shadow
  const handlePaidAmount = (st_order_id = null) => {
    setIsLoading(true);
    if (st_order_id) {
      payload.send_receipt = false;
    }
    apiPut(`/scheduled-work/mark-as-paid/${id}`, payload)
      .then((response) => {
        setOpenMarkAsPaidModal(false);
        setSendReceipt(true);
        setIsLoading(false);
        if (response.Status === 'Success') {
          notify(response.Message);
          refresh();
        } else if (response.error_code === 'AMAZON_ALREADY_COMPLETED') {
          setMarkCompleteError(response.Message || 'This order is already completed in Amazon.');
        } else {
          notify(response.Message, 'warning');
        }
        rudderStackEvent('Confirm', id);
      })
      .catch((error) => {
        setOpenMarkAsPaidModal(false);
        setSendReceipt(true);
        setIsLoading(false);
        notify(`Error: ${error}`, 'warning');
      });
  };

  const markAsCompleteBtnEnable = () => {
    if (
      st_order_id !== '0' &&
      st_order_id != null &&
      (payment_method === null || payment_method !== null)
    ) {
      return true;
    }
    if (
      (st_order_id === null || st_order_id === '0') &&
      payment_method !== null
    ) {
      return true;
    }
    if (
      (st_order_id === null || st_order_id === '0') &&
      payment_method === null
    ) {
      return false;
    }
  };

  const openSimpleShare = () => {
    setIsViewPaymentLoading(true);
    setViewPaymentError('');

    const acceptedValues = ['Amazon', 'AmazonMP'];
    const isSourceContainAmazon = acceptedValues.includes(source);

    let data = '';
    if (
      isSourceContainAmazon &&
      service_order_id !== '0' &&
      service_order_id !== null
    ) {
      data = { serviceOrderId: service_order_id };
    } else if (st_order_id !== '') {
      data = { orderId: st_order_id };
    }

    apiGet(`/payment-status`, data, true)
      .then((res) => {
        if (res?.Status === 'Failure') {
          setViewPaymentError(res.Message);
          return;
        }
        const params =
          isSourceContainAmazon &&
          service_order_id !== '0' &&
          service_order_id !== null
            ? `serviceOrderId=${service_order_id}&redirectTo=${res}`
            : `orderId=${st_order_id}&redirectTo=${res}`;
        console.log('params', params);
        window.parent.postMessage(
          params,
          window.location.host.includes('staging')
            ? 'https://simple360-temp-staging-branch-simpletire.vercel.app/'
            : 'https://app.simple360.com',
        );
      })
      .catch(() => {
        setViewPaymentError('Something went wrong. Please try again.');
      })
      .finally(() => {
        setIsViewPaymentLoading(false);
      });
  };

  const amount = Math.abs(
    amount_due !== 0
      ? amount_due
      : total_taxed_price_dollars - st_to_installer_amount,
  );

  const amount_paid_due_label = status === 'finished' ? 'paid' : 'due';
  return (
    <>
      <FormWithRedirect
        resource="scheduled-work"
        record={record}
        save={handleSubmit}
        render={(formProps) => (
          <>
            <Grid container spacing={0} className={classes.titleMargin}>
              <Grid item xs={10} sm={10} md={10}>
                <Typography variant="h5" gutterBottom>
                  Payment
                </Typography>
              </Grid>
              {/* {activePaymentMethods?.length > 0 && */}
              {/*  !isEditing && */}
              {/*  status !== 'finished' && */}
              {/*  status !== 'cancelled' && ( */}
              {/*    <Grid item xs={2} sm={2} md={2} className={classes.textRight}> */}
              {/*      <EditIcon */}
              {/*        fontSize="inherit" */}
              {/*        className={classes.pointIcon} */}
              {/*        onClick={() => setIsEditing(true)} */}
              {/*      /> */}
              {/*    </Grid> */}
              {/*  )} */}
            </Grid>
            <Grid container spacing={0}>
              <Grid item md={12} sm={12} xs={12} className={classes.actionBox}>
                {isEditing && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                  >
                    <Button
                      label="ra.action.cancel"
                      onClick={() => setIsEditing(false)}
                      className={classes.btnCancel}
                    >
                      <IconCancel />
                    </Button>
                    <SaveButton
                      handleSubmitWithRedirect={
                        formProps.handleSubmitWithRedirect
                      }
                      saving={formProps.loading}
                      disabled={formProps.pristine}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item md={12}>
                {isEditing ? (
                  <Box
                    display="none"
                    className="schedule_work_section filter_items"
                  >
                    <SelectInput
                      source="payment_method_id"
                      allowEmpty
                      choices={activePaymentMethods.map(
                        ({ payment_method_id, short_description: name }) => ({
                          id: payment_method_id,
                          name,
                        }),
                      )}
                    />
                  </Box>
                ) : (
                  <Box display="none" my={1} className={classes.boxMargin}>
                    <Box>
                      <DollarSignIcon className={classes.iconMargin} />
                    </Box>
                    <Box>
                      <Typography variant="body2" className={classes.listColor}>
                        <span className={classes.listLabel}>
                          Payment Method
                        </span>
                      </Typography>
                      <Typography variant="body2" className={classes.listColor}>
                        {short_description}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {is_square && square_card_id && hasSquareCard && (
                  <Box display="flex" my={1} className={classes.boxMargin}>
                    <Box>
                      <CreditCardIcon className={classes.iconMargin} />
                    </Box>
                    <Box>
                      <Typography variant="body2" className={classes.listColor}>
                        {square_brand} x{square_last4}
                      </Typography>
                      <ButtonUI
                        size="small"
                        color="primary"
                        onClick={() => setIsCardModalOpen(true)}
                      >
                        Change
                      </ButtonUI>
                    </Box>
                  </Box>
                )}
                {((short_description !== 'Cash' &&
                  payment_method !== null &&
                  status !== 'finished' &&
                  status !== 'cancelled') ||
                  (is_square &&
                    !square_card_id &&
                    status !== 'finished' &&
                    status !== 'cancelled')) &&
                  isUsingSquare && (
                    <Box textAlign="center">
                      <ButtonUI
                        size="small"
                        variant="contained"
                        color="primary"
                        className={classes.creditCardButton}
                        onClick={() => setIsCardModalOpen(true)}
                      >
                        Add Credit Card
                        <CreditCardIcon />
                      </ButtonUI>
                    </Box>
                  )}
              </Grid>
            </Grid>
            <PaidOnlineCard services={services} />
            <div className="border_bottom" />

            <Grid item xs={12} sm={12} md={12} className={classes.payoutInfo}>
              <Typography variant="h5" gutterBottom align="center">
                Payout Information
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                className={classes.boxMargin}
              >
                <Box>
                  <DollarSignIcon className={classes.dollarIconStyle} />
                </Box>
                <Box>
                  <Typography variant="h2">
                    <span className={classes.payoutPrice}>
                      {total_taxed_price_dollars}
                      {/* {amount_paid_dollars} */}
                      {/* {st_to_installer_amount != 0
                        ? st_to_installer_amount
                        : amount_paid_dollars} */}
                    </span>
                  </Typography>
                </Box>
              </Box>

              {amount != 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginTop: '12px',
                    marginBottom: '24px',
                    width: '90%',
                    marginLeft: '20px',
                  }}
                >
                  <span
                    style={{
                      color: '#637381',
                      fontSize: '14px',
                      fontWeight: 450,
                      padding: '4px 12px',
                    }}
                  >
                    Amount {amount_paid_due_label} at the shop
                  </span>
                  <span
                    style={{
                      backgroundColor: '#fee2e2',
                      color: '#b91c1c',
                      fontWeight: 600,
                      fontSize: '14px',
                      padding: '4px 12px',
                      borderRadius: '8px',
                    }}
                  >
                    ${amount_due.toFixed(2)}
                  </span>
                </div>
              )}
              <Box align="center">
                {
                  isFromS360 &&
                // hasPaidInstall &&
                status === 'finished' &&
                  amount_paid_dollars === total_taxed_price_dollars &&
                  hasNewTireInstallation &&
                  (!!st_order_id || !!service_order_id) && (
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                      textAlign="center"
                    >
                      <Button
                        label=""
                        onClick={openSimpleShare}
                        disabled={isViewPaymentLoading}
                        className={classes.markPaidButton}
                        style={{ marginRight: 0 }}
                      >
                        <>
                          {isViewPaymentLoading ? (
                            <CircularProgress
                              size={18}
                              color="inherit"
                              style={{ marginRight: 8 }}
                            />
                          ) : null}
                          View Payment
                        </>
                      </Button>
                      <Typography
                        color="#637381"
                        fontSize="12px"
                        style={{ textAlign: 'end', width: '85%' }}
                      >
                        Via SimpleShare
                      </Typography>
                      {viewPaymentError && (
                        <Box
                          mt={1}
                          display="flex"
                          alignItems="center"
                          style={{
                            backgroundColor: '#FFF2F0',
                            border: '1px solid #FFCCC7',
                            borderRadius: 8,
                            padding: '8px 12px',
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: '#B71D18',
                              flex: 1,
                              textAlign: 'left',
                            }}
                          >
                            {viewPaymentError}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setViewPaymentError('')}
                            style={{
                              color: '#B71D18',
                              padding: 2,
                              marginLeft: 8,
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                {status !== 'cancelled' &&
                  status !== 'finished' &&
                  markAsCompleteBtnEnable() && (
                    <>
                    <Button
                      label=""
                      onClick={() => { setMarkCompleteError(''); completeRepairOrder(st_order_id); }}
                      className={classes.markPaidButton}
                    >
                      <>
                        Mark as Complete
                        <svg
                          className={classes.markBtnIconStyle}
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M14.8 9C14.6188 8.68577 14.3557 8.42671 14.0386 8.25056C13.7215 8.07441 13.3625 7.9878 13 8H11C10.4696 8 9.96086 8.21072 9.58579 8.58579C9.21071 8.96086 9 9.46957 9 10C9 10.5304 9.21071 11.0391 9.58579 11.4142C9.96086 11.7893 10.4696 12 11 12H13C13.5304 12 14.0391 12.2107 14.4142 12.5858C14.7893 12.9609 15 13.4696 15 14C15 14.5304 14.7893 15.0391 14.4142 15.4142C14.0391 15.7893 13.5304 16 13 16H11C10.6375 16.0122 10.2785 15.9256 9.96142 15.7495C9.64435 15.5733 9.38115 15.3142 9.2 15M12 7V17M3 12C3 13.1819 3.23279 14.3522 3.68508 15.4442C4.13738 16.5361 4.80031 17.5282 5.63604 18.364C6.47177 19.1997 7.46392 19.8626 8.55585 20.3149C9.64778 20.7672 10.8181 21 12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 10.8181 20.7672 9.64778 20.3149 8.55585C19.8626 7.46392 19.1997 6.47177 18.364 5.63604C17.5282 4.80031 16.5361 4.13738 15.4442 3.68508C14.3522 3.23279 13.1819 3 12 3C10.8181 3 9.64778 3.23279 8.55585 3.68508C7.46392 4.13738 6.47177 4.80031 5.63604 5.63604C4.80031 6.47177 4.13738 7.46392 3.68508 8.55585C3.23279 9.64778 3 10.8181 3 12Z"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </>
                    </Button>
                    {markCompleteError && (
                      <Box
                        mt={1}
                        display="flex"
                        alignItems="center"
                        style={{
                          backgroundColor: '#FFF2F0',
                          border: '1px solid #FFCCC7',
                          borderRadius: 8,
                          padding: '8px 12px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{ color: '#B71D18', flex: 1, textAlign: 'left' }}
                        >
                          {markCompleteError}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setMarkCompleteError('')}
                          style={{ color: '#B71D18', padding: 2, marginLeft: 8 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    </>
                  )}
              </Box>
              {isEditing && (
                <Box textAlign="center" display="none">
                  <Button
                    label=""
                    className={classes.cardDetailBtn}
                    onClick={() => setIsCardModalDetailOpen(true)}
                  >
                    <>
                      View Card Details
                      <svg
                        className={classes.cardDetailBtnIcon}
                        xmlns="http://www.w3.org/2000/svg"
                        width="1em"
                        height="1em"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="white"
                          d="M10 20h4c3.771 0 5.657 0 6.828-1.172S22 15.771 22 12c0-.442-.002-1.608-.004-2H2c-.002.392 0 1.558 0 2c0 3.771 0 5.657 1.171 6.828S6.23 20 10 20"
                          opacity="0.5"
                        />
                        <path
                          fill="white"
                          d="M9.995 4h4.01c3.781 0 5.672 0 6.846 1.116c.846.803 1.083 1.96 1.149 3.884v1H2V9c.066-1.925.303-3.08 1.149-3.884C4.323 4 6.214 4 9.995 4M12.5 15.25a.75.75 0 0 0 0 1.5H14a.75.75 0 0 0 0-1.5zm-6.5 0a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5z"
                        />
                      </svg>
                    </>
                  </Button>
                </Box>
              )}
            </Grid>
          </>
        )}
      />

      {/* Card Modal */}
      <Dialog open={isCardModalOpen} onClose={closeModal}>
        <DialogTitle onClose={closeModal}>Add Credit Card</DialogTitle>
        <Divider />
        <DialogContent>
          <SquareCCInput
            card={card}
            setCard={setCard}
            setShow={setIsCardModalOpen}
            setHasError={setHasError}
          />
          {errorMessage && (
            <Typography color="error">Error: {errorMessage}</Typography>
          )}
        </DialogContent>
        <DialogActions className={classes.actionButtons}>
          <SaveButton
            icon={<></>}
            style={{ width: '100%' }}
            label="Authorize"
            disabled={!card || hasError || isSaving}
            handleSubmitWithRedirect={() => submitCard(card)}
            saving={isSaving}
          />
        </DialogActions>
      </Dialog>

      {/* Card Modal detail */}
      <Dialog
        open={isCardModalDetailOpen}
        onClose={closeDetailModal}
        fullWidth="true"
        className={classes.cardDetailDialog}
      >
        <DialogTitle
          className={classes.cardDetailTitle}
          onClose={closeDetailModal}
        >
          Mastrer Card - Credit Card Info
        </DialogTitle>
        <DialogContent>
          <Box className={classes.cardDetailBox}>
            <Typography variant="h5" gutterBottom>
              Card Number
            </Typography>
            <Typography variant="body2">54646546546</Typography>
          </Box>

          <Box className={classes.cardDetailBox}>
            <Typography variant="h5" gutterBottom>
              Expiration Date
            </Typography>
            <Typography variant="body2">07/25</Typography>
          </Box>

          <Box className={classes.cardDetailBox}>
            <Typography variant="h5" gutterBottom>
              Name on Card
            </Typography>
            <Typography variant="body2">Test Name</Typography>
          </Box>

          <Box className={classes.cardDetailBox}>
            <Typography variant="h5" gutterBottom>
              CVC
            </Typography>
            <Typography variant="body2">12345</Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={openMarkAsPaidModal}>
        <DialogContent className={classes.amountPaidDialog}>
          <div>
            Set Amount Paid to {formatPrice(total_taxed_price_dollars)}?
          </div>
          {!st_order_id && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={sendReceipt}
                  onChange={() => setSendReceipt(!sendReceipt)}
                />
              }
              label="Send Receipt To Customer"
            />
          )}

          {googleReviewUrl && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={sendGoogleReview}
                  onChange={() => setSendGoogleReview(!sendGoogleReview)}
                />
              }
              label="Request Google Review via SMS"
            />
          )}
        </DialogContent>
        <DialogActions>
          <ButtonUI onClick={() => handleCancelReceiptModel()} color="primary">
            Cancel
          </ButtonUI>
          <ButtonUI
            color="primary"
            variant="contained"
            onClick={() => handlePaidAmount()}
          >
            {!isLoading && <CheckIcon style={{ marginRight: '7px' }} />}
            {isLoading ? 'Updating...' : 'Confirm'}
          </ButtonUI>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnifiedPayment;
